import _ from 'lodash';
import moment from 'moment-timezone';
import { TZ } from '../../../../shared/consts';
import {
  INPUT_FMT, AUDIENCE_STATS, GRANULARITY_TO_DURATION,
} from '../../common/constants';

// The followers param for all the functions in this file contains *contributor*.
// It is named `followers` to follow the context state variables' naming
// convention.

const createMomTz = d => moment.tz(d, INPUT_FMT, TZ);

const isValid = d => moment(d, INPUT_FMT, true).isValid();

const dateHashToArray = hash => _(hash)
  .map((v, date) => ({ ...v, date }))
  .filter(({ date }) => isValid(date))
  .value();

const propsReducer = props => (r, item) => {
  const d = { ...r };
  props.forEach(prop => {
    d[prop] += _.get(item, prop, 0);
  });
  return d;
};

const getTotalFigures = followers => _(followers)
  .map(c => {
    const stats = _.sortBy(dateHashToArray(c.followers), ['date']);
    const initial = { total: _.get(_.last(stats), 'accumTotal', 0), gained: 0, lost: 0 };
    return stats.reduce(propsReducer(AUDIENCE_STATS), initial);
  })
  .reduce(propsReducer(['total'].concat(AUDIENCE_STATS)), { total: 0, gained: 0, lost: 0 });

const earliestStatsUnit = ({ followers, granularity = 'd' } = {}) => {
  let earliestDate = null;
  let earliestContributorCreatedDate = moment.tz(TZ);

  (followers || []).forEach(contributor => {
    // createdAt is in ISO96001 format, which already contains timezone
    // offset. Do NOT use `moment.tz` on this string.
    const createdDate = moment(contributor.createdAt);
    if (createdDate.isBefore(earliestContributorCreatedDate)) {
      earliestContributorCreatedDate = undefined;
      earliestContributorCreatedDate = createdDate;
    }
    const date = _.first(Object.keys(contributor.followers || []).filter(isValid).sort());
    if (date) {
      const mom = createMomTz(date);
      // always use negative isSameOrAfter to cope with the initial null
      // value
      if (!mom.isSameOrAfter(earliestDate)) {
        earliestDate = undefined;
        earliestDate = mom;
      }
    }
  });

  const result = moment().tz(TZ).diff(
    earliestDate ? moment(earliestDate, INPUT_FMT, TZ) : earliestContributorCreatedDate,
    GRANULARITY_TO_DURATION[granularity] || 'days',
  );

  return result;
};

const fillDateHash = (h, from, to) => {
  let lastValue = 0;
  const result = {};
  const t = createMomTz(from);
  while (!t.isAfter(to, 'day')) {
    const thisDay = t.format(INPUT_FMT);
    result[thisDay] = _.get(h, `${thisDay}.accumTotal`, lastValue);
    lastValue = result[thisDay];
    t.add(1, 'day');
  }
  return result;
};

const getDateBoundary = followers => {
  const boundary = {
    from: moment.tz().subtract(30, 'days').format(INPUT_FMT),
    to: moment.tz().format(INPUT_FMT),
  };
  (followers || []).forEach(c => {
    Object.keys(c.followers).forEach(d => {
      boundary.from = d < boundary.from ? d : boundary.from;
      boundary.to = d > boundary.to ? d : boundary.to;
    });
  });
  return boundary;
};

const followersToTimeSeries = followers => {
  const boundary = getDateBoundary(followers);
  const stats = _.map(followers, c => fillDateHash(c.followers, boundary.from, boundary.to));
  const timeseries = {};
  Object.keys(stats[0] || {}).forEach(date => {
    timeseries[date] = _.reduce(stats, (v, item) => v + item[date], 0);
  });
  return timeseries;
};

const timeseriesToBucket = ({
  ts, start, end, granularity,
}) => {
  const result = [];
  const duration = GRANULARITY_TO_DURATION[granularity] || 'days';

  // With start and end being days away from today, we're using a range like:
  //   (start, end]
  // That means start of day of 1 day after start date to end of day end date.
  const t = moment()
    .tz(TZ)
    .add(1, duration)
    .startOf(duration)
    .subtract(start, duration);

  let pos = start;
  while (pos > end) {
    const startDay = t.format(INPUT_FMT);
    const singleBucket = {
      time: startDay,
      changed: 0,
      value: _.get(ts, startDay, _.get(_.last(result), 'value', 0)),
    };
    if (granularity === 'm') {
      let monthEnd = 31;
      while (monthEnd > 0) {
        const day = t.clone().add(monthEnd, 'days');
        if (day.isSame(t, 'month')) {
          const value = _.get(ts, day.format(INPUT_FMT));
          if (typeof value === 'number') {
            singleBucket.value = value;
            break;
          }
        }
        monthEnd -= 1;
      }
    } else if (granularity === 'w') {
      let weekEnd = 6;
      while (weekEnd > 0) {
        const value = _.get(ts, t.clone().add(weekEnd, 'days').format(INPUT_FMT));
        if (typeof value === 'number') {
          singleBucket.value = value;
          break;
        }
        weekEnd -= 1;
      }
    }
    singleBucket.changed = result.length ? (singleBucket.value - _.last(result).value) : 0;
    result.push(singleBucket);
    pos -= 1;
    t.add(1, duration);
  }

  return result;
};

const followersToBucket = ({
  followers, start, end, granularity,
}) => {
  const ts = followersToTimeSeries(followers);
  return timeseriesToBucket({
    ts, start, end, granularity,
  });
};

export {
  earliestStatsUnit,
  followersToBucket,
  getTotalFigures,
};
